import type { Lot } from '../data/mockLots';

type RarityInput = Pick<Lot, 'rarity' | 'rarityColor'>;

const RARITY_BASE: Record<NonNullable<Lot['rarity']>, { label: string; color: string }> = {
  consumer: { label: 'CONSUMER', color: '#b0c3d9' },
  industrial: { label: 'INDUSTRIAL', color: '#5e98d9' },
  milspec: { label: 'MIL-SPEC', color: '#4b69ff' },
  restricted: { label: 'RESTRICTED', color: '#8847ff' },
  classified: { label: 'CLASSIFIED', color: '#d32ce6' },
  covert: { label: 'COVERT', color: '#eb4b4b' },
  contraband: { label: 'CONTRABAND', color: '#e4ae39' },
};

const COLOR_TO_LABEL: Record<string, string> = {
  '#b0c3d9': 'CONSUMER',
  '#5e98d9': 'INDUSTRIAL',
  '#4b69ff': 'MIL-SPEC',
  '#8847ff': 'RESTRICTED',
  '#d32ce6': 'CLASSIFIED',
  '#eb4b4b': 'COVERT',
  '#e4ae39': 'CONTRABAND',
};

const RARITY_FALLBACK = '#7a8595';

// Resolves the display label + accent color for a lot's rarity. A real API lot
// carries an authoritative `rarityColor` (the in-game hex) but no label; a mock
// lot carries the `rarity` enum but no color. We reconcile both: color wins for
// the swatch, and the label is reverse-looked-up from the color when present.
export function resolveRarity(lot: RarityInput): { label: string; color: string } {
  const base = lot.rarity ? RARITY_BASE[lot.rarity] : null;
  const color = lot.rarityColor ?? base?.color ?? RARITY_FALLBACK;
  const label = lot.rarityColor
    ? (COLOR_TO_LABEL[lot.rarityColor.toLowerCase()] ??
      base?.label ??
      lot.rarity?.toUpperCase() ??
      'UNRANKED')
    : (base?.label ?? 'UNRANKED');
  return { label, color };
}

// StatTrak™ / Souvenir are mutually exclusive special finishes with fixed
// in-game accent colors. `short`/`tm` drive the compact inline title prefix
// (e.g. "ST™ Redline"); `label` is the full form for badges. Returns null for a
// plain skin.
export function resolveFinish(
  lot: Pick<Lot, 'isStatTrak' | 'isSouvenir'>,
): { label: string; short: string; tm: boolean; color: string } | null {
  if (lot.isStatTrak) return { label: 'StatTrak™', short: 'ST', tm: true, color: '#cf6a32' };
  if (lot.isSouvenir) return { label: 'Souvenir', short: 'SV', tm: false, color: '#ffd700' };
  return null;
}
